import React, { createContext, useContext, useReducer, useCallback, ReactNode, useMemo, useEffect, useRef } from 'react';
import { useUrlState, UrlState } from '../hooks/useUrlState';
import type {
  ForecastV3State,
  ForecastV3Action,
  ForecastVersion,
  ForecastAccount,
  ActiveTab,
  DetailPanelTab,
  FilterState,
  MonthStatus,
} from '../types';

// ============ Initial State ============

const initialFilterState: FilterState = {
  departments: [],
  accountTypes: [],
  driverTypes: [],
  showOnlyOverrides: false,
  showNeedsAttention: false,
  searchTerm: '',
};

const initialState: ForecastV3State = {
  orgId: null,
  locationId: null,
  year: new Date().getFullYear(),

  // Version context - single version per year with month-level locking
  currentVersion: null,
  activeMonth: null, // Currently focused month for UI highlighting

  // UI state
  activeTab: 'workspace',
  dateRange: { start: '', end: '' },
  selectedAccount: null,
  expandedSections: { revenue: true, expense: true },
  expandedColumns: {}, // Track which month columns are expanded (0-11)

  // Bulk selection state
  isBulkMode: false,
  selectedAccountIds: [],
  bulkOperationInProgress: false,

  isDetailPanelOpen: false,
  detailPanelTab: 'driver',
  filters: initialFilterState,
  isLoading: false,
  isRecalculating: false,
  isSaving: false,
  compareVersionA: null,
  compareVersionB: null,

  // Pending account ID from URL (to be selected when data loads)
  pendingAccountId: null,
  // Pending detail panel tab from URL (to be applied after account selection)
  pendingDetailPanelTab: null,

  // Pending department from Notes page navigation
  pendingDepartmentName: null,
  pendingDepartmentSection: null,

  // Fullscreen mode
  isFullscreen: false,

  // Refresh token
  refreshToken: 0,
};

// ============ Reducer ============

function forecastV3Reducer(state: ForecastV3State, action: ForecastV3Action): ForecastV3State {
  switch (action.type) {
    case 'SET_ORG_ID':
      return { ...state, orgId: action.payload };

    case 'SET_LOCATION_ID':
      return { ...state, locationId: action.payload };

    case 'SET_YEAR':
      return { ...state, year: action.payload };

    case 'SET_VERSION':
      return { ...state, currentVersion: action.payload };

    case 'SET_ACTIVE_MONTH':
      return { ...state, activeMonth: action.payload };

    case 'UPDATE_LOCKED_MONTHS':
      // Update the lockedMonths array on the current version
      if (!state.currentVersion) return state;
      return {
        ...state,
        currentVersion: {
          ...state.currentVersion,
          lockedMonths: action.payload,
        },
      };

    case 'SET_ACTIVE_TAB':
      return {
        ...state,
        activeTab: action.payload,
        // Close detail panel when switching tabs
        isDetailPanelOpen: action.payload === 'workspace' ? state.isDetailPanelOpen : false,
      };

    case 'SET_DATE_RANGE':
      return { ...state, dateRange: action.payload };

    case 'SELECT_ACCOUNT':
      return {
        ...state,
        selectedAccount: action.payload,
        isDetailPanelOpen: action.payload !== null,
        detailPanelTab: 'driver', // Reset to driver tab when selecting new account
      };

    case 'UPDATE_SELECTED_ACCOUNT_DATA':
      // Update the selected account data without changing panel state or tab
      // Used when syncing with fresh data after refetch
      return {
        ...state,
        selectedAccount: action.payload,
      };

    case 'TOGGLE_SECTION':
      return {
        ...state,
        expandedSections: {
          ...state.expandedSections,
          [action.payload]: !state.expandedSections[action.payload],
        },
      };

    case 'TOGGLE_DETAIL_PANEL':
      return {
        ...state,
        isDetailPanelOpen: action.payload !== undefined ? action.payload : !state.isDetailPanelOpen,
      };

    case 'SET_DETAIL_PANEL_TAB':
      return { ...state, detailPanelTab: action.payload };

    case 'SET_FILTERS':
      return {
        ...state,
        filters: { ...state.filters, ...action.payload },
      };

    case 'CLEAR_FILTERS':
      return { ...state, filters: initialFilterState };

    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_RECALCULATING':
      return { ...state, isRecalculating: action.payload };

    case 'SET_SAVING':
      return { ...state, isSaving: action.payload };

    case 'SET_COMPARE_VERSIONS':
      return {
        ...state,
        compareVersionA: action.payload.a,
        compareVersionB: action.payload.b,
      };

    case 'SET_PENDING_ACCOUNT_ID':
      return { ...state, pendingAccountId: action.payload };

    case 'SET_PENDING_DETAIL_PANEL_TAB':
      return { ...state, pendingDetailPanelTab: action.payload };

    case 'SET_PENDING_DEPARTMENT':
      return {
        ...state,
        pendingDepartmentName: action.payload?.name ?? null,
        pendingDepartmentSection: action.payload?.section ?? null,
      };

    case 'SET_FULLSCREEN':
      return { ...state, isFullscreen: action.payload };

    case 'TRIGGER_REFRESH':
      return { ...state, refreshToken: state.refreshToken + 1 };

    // Bulk selection actions
    case 'TOGGLE_BULK_MODE':
      return {
        ...state,
        isBulkMode: action.payload !== undefined ? action.payload : !state.isBulkMode,
        // Clear selection when exiting bulk mode
        selectedAccountIds: action.payload === false ? [] : state.selectedAccountIds,
      };

    case 'TOGGLE_ACCOUNT_SELECTION': {
      const accountId = action.payload;
      const isSelected = state.selectedAccountIds.includes(accountId);
      return {
        ...state,
        selectedAccountIds: isSelected
          ? state.selectedAccountIds.filter(id => id !== accountId)
          : [...state.selectedAccountIds, accountId],
      };
    }

    case 'SET_SELECTED_ACCOUNTS':
      return { ...state, selectedAccountIds: action.payload };

    case 'ADD_SELECTED_ACCOUNTS':
      return {
        ...state,
        selectedAccountIds: Array.from(new Set([...state.selectedAccountIds, ...action.payload])),
      };

    case 'CLEAR_BULK_SELECTION':
      return { ...state, selectedAccountIds: [] };

    case 'SET_BULK_OPERATION_IN_PROGRESS':
      return { ...state, bulkOperationInProgress: action.payload };

    // Column expansion actions
    case 'TOGGLE_COLUMN_EXPANSION':
      return {
        ...state,
        expandedColumns: {
          ...state.expandedColumns,
          [action.payload]: !state.expandedColumns[action.payload],
        },
      };

    case 'SET_ALL_COLUMNS_EXPANDED': {
      const newExpanded: Record<number, boolean> = {};
      if (action.payload) {
        // Expand all 12 months (0-11)
        for (let i = 0; i < 12; i++) {
          newExpanded[i] = true;
        }
      }
      // If payload is false, newExpanded stays empty (all collapsed)
      return { ...state, expandedColumns: newExpanded };
    }

    default:
      return state;
  }
}

// ============ Context Types ============

interface ForecastV3ContextValue {
  state: ForecastV3State;
  dispatch: React.Dispatch<ForecastV3Action>;

  // Convenience actions
  setOrgId: (orgId: number | null) => void;
  setLocationId: (locationId: number | null) => void;
  setYear: (year: number) => void;
  setVersion: (version: ForecastVersion | null) => void;
  setActiveMonth: (month: number | null) => void;
  updateLockedMonths: (lockedMonths: number[]) => void;
  setActiveTab: (tab: ActiveTab) => void;
  setDateRange: (start: string, end: string) => void;
  selectAccount: (account: ForecastAccount | null) => void;
  updateSelectedAccountData: (account: ForecastAccount | null) => void;  // Update account data without resetting tab
  toggleSection: (sectionId: string) => void;
  toggleDetailPanel: (open?: boolean) => void;
  setDetailPanelTab: (tab: DetailPanelTab) => void;
  setFilters: (filters: Partial<FilterState>) => void;
  clearFilters: () => void;
  setLoading: (loading: boolean) => void;
  setRecalculating: (recalculating: boolean) => void;
  setSaving: (saving: boolean) => void;
  setCompareVersions: (a: ForecastVersion | null, b: ForecastVersion | null) => void;
  setPendingAccountId: (accountId: number | null) => void;
  setPendingDetailPanelTab: (tab: DetailPanelTab | null) => void;
  setPendingDepartment: (department: { name: string; section: 'Revenue' | 'Expense' } | null) => void;
  setFullscreen: (fullscreen: boolean) => void;
}

// ============ Context ============

export const ForecastV3Context = createContext<ForecastV3ContextValue | undefined>(undefined);

// ============ Provider ============

interface ForecastV3ProviderProps {
  children: ReactNode;
  initialOrgId?: number | null;
  initialLocationId?: number | null;
  initialYear?: number;
  initialVersion?: ForecastVersion;
  initialDateRange?: { start: string; end: string };
}

export function ForecastV3Provider({
  children,
  initialOrgId,
  initialLocationId,
  initialYear,
  initialVersion,
  initialDateRange,
}: ForecastV3ProviderProps) {
  const [state, dispatch] = useReducer(forecastV3Reducer, {
    ...initialState,
    orgId: initialOrgId ?? null,
    locationId: initialLocationId ?? null,
    year: initialYear ?? new Date().getFullYear(),
    currentVersion: initialVersion || null,
    dateRange: initialDateRange || initialState.dateRange,
  });

  // Convenience action creators
  const setOrgId = useCallback((orgId: number | null) => {
    dispatch({ type: 'SET_ORG_ID', payload: orgId });
  }, []);

  const setLocationId = useCallback((locationId: number | null) => {
    dispatch({ type: 'SET_LOCATION_ID', payload: locationId });
  }, []);

  const setYear = useCallback((year: number) => {
    dispatch({ type: 'SET_YEAR', payload: year });
  }, []);

  const setVersion = useCallback((version: ForecastVersion | null) => {
    dispatch({ type: 'SET_VERSION', payload: version });
  }, []);

  const setActiveMonth = useCallback((month: number | null) => {
    dispatch({ type: 'SET_ACTIVE_MONTH', payload: month });
  }, []);

  const updateLockedMonths = useCallback((lockedMonths: number[]) => {
    dispatch({ type: 'UPDATE_LOCKED_MONTHS', payload: lockedMonths });
  }, []);

  const setActiveTab = useCallback((tab: ActiveTab) => {
    dispatch({ type: 'SET_ACTIVE_TAB', payload: tab });
  }, []);

  const setDateRange = useCallback((start: string, end: string) => {
    dispatch({ type: 'SET_DATE_RANGE', payload: { start, end } });
  }, []);

  const selectAccount = useCallback((account: ForecastAccount | null) => {
    dispatch({ type: 'SELECT_ACCOUNT', payload: account });
  }, []);

  // Update the selected account data without changing panel state or tab
  // Used when syncing with fresh data after refetch
  const updateSelectedAccountData = useCallback((account: ForecastAccount | null) => {
    dispatch({ type: 'UPDATE_SELECTED_ACCOUNT_DATA', payload: account });
  }, []);

  const toggleSection = useCallback((sectionId: string) => {
    dispatch({ type: 'TOGGLE_SECTION', payload: sectionId });
  }, []);

  const toggleDetailPanel = useCallback((open?: boolean) => {
    dispatch({ type: 'TOGGLE_DETAIL_PANEL', payload: open });
  }, []);

  const setDetailPanelTab = useCallback((tab: DetailPanelTab) => {
    dispatch({ type: 'SET_DETAIL_PANEL_TAB', payload: tab });
  }, []);

  const setFilters = useCallback((filters: Partial<FilterState>) => {
    dispatch({ type: 'SET_FILTERS', payload: filters });
  }, []);

  const clearFilters = useCallback(() => {
    dispatch({ type: 'CLEAR_FILTERS' });
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  }, []);

  const setRecalculating = useCallback((recalculating: boolean) => {
    dispatch({ type: 'SET_RECALCULATING', payload: recalculating });
  }, []);

  const setSaving = useCallback((saving: boolean) => {
    dispatch({ type: 'SET_SAVING', payload: saving });
  }, []);

  const setCompareVersions = useCallback((a: ForecastVersion | null, b: ForecastVersion | null) => {
    dispatch({ type: 'SET_COMPARE_VERSIONS', payload: { a, b } });
  }, []);

  const setPendingAccountId = useCallback((accountId: number | null) => {
    dispatch({ type: 'SET_PENDING_ACCOUNT_ID', payload: accountId });
  }, []);

  const setPendingDetailPanelTab = useCallback((tab: DetailPanelTab | null) => {
    dispatch({ type: 'SET_PENDING_DETAIL_PANEL_TAB', payload: tab });
  }, []);

  const setPendingDepartment = useCallback((department: { name: string; section: 'Revenue' | 'Expense' } | null) => {
    dispatch({ type: 'SET_PENDING_DEPARTMENT', payload: department });
  }, []);

  const setFullscreen = useCallback((fullscreen: boolean) => {
    dispatch({ type: 'SET_FULLSCREEN', payload: fullscreen });
  }, []);

  const value: ForecastV3ContextValue = {
    state,
    dispatch,
    setOrgId,
    setLocationId,
    setYear,
    setVersion,
    setActiveMonth,
    updateLockedMonths,
    setActiveTab,
    setDateRange,
    selectAccount,
    updateSelectedAccountData,
    toggleSection,
    toggleDetailPanel,
    setDetailPanelTab,
    setFilters,
    clearFilters,
    setLoading,
    setRecalculating,
    setSaving,
    setCompareVersions,
    setPendingAccountId,
    setPendingDetailPanelTab,
    setPendingDepartment,
    setFullscreen,
  };

  return (
    <ForecastV3Context.Provider value={value}>
      {children}
    </ForecastV3Context.Provider>
  );
}

// ============ Hook ============

export function useForecastV3() {
  const context = useContext(ForecastV3Context);
  if (context === undefined) {
    throw new Error('useForecastV3 must be used within a ForecastV3Provider');
  }
  return context;
}

// ============ Selector Hooks ============

export function useForecastV3State() {
  const { state } = useForecastV3();
  return state;
}

export function useCurrentVersion() {
  const { state } = useForecastV3();
  return state.currentVersion;
}

/**
 * Hook for month-level operations within the current version.
 * Provides helpers to determine month status and editability.
 */
export function useMonthlyVersions() {
  const { state, setActiveMonth, updateLockedMonths } = useForecastV3();
  const { currentVersion, activeMonth, year } = state;

  // Derive month statuses from the version's lockedMonths array
  const lockedMonths = currentVersion?.lockedMonths || [];
  const currentMonth = new Date().getMonth() + 1; // 1-12
  const currentYear = new Date().getFullYear();

  // Get status for a specific month
  const getMonthStatus = useCallback((month: number): MonthStatus => {
    if (!currentVersion) return 'none';
    if (lockedMonths.includes(month)) return 'locked';
    // Budgets are plans, not projections — every unlocked month is an editable
    // draft (and lockable from the header) regardless of where it falls
    // relative to today.
    if (currentVersion.type === 'Budget') return 'draft';
    // If we're looking at a future year, all months are projected
    if (year > currentYear) return 'projected';
    // If we're looking at current year, months after current month are projected
    if (year === currentYear && month > currentMonth) return 'projected';
    // Otherwise it's a draft (editable)
    return 'draft';
  }, [currentVersion, lockedMonths, year, currentYear, currentMonth]);

  // Check if a month is editable (not locked and not purely projected)
  const isMonthEditable = useCallback((month: number): boolean => {
    const status = getMonthStatus(month);
    return status === 'draft';
  }, [getMonthStatus]);

  // Build MonthVersionInfo array for backward compatibility with PnLGrid
  const monthVersions = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const month = i + 1;
      return {
        month,
        version: currentVersion,
        status: getMonthStatus(month),
      };
    });
  }, [currentVersion, getMonthStatus]);

  return {
    activeMonth,
    monthVersions, // For backward compatibility
    setActiveMonth,
    updateLockedMonths,
    lockedMonths,
    getMonthStatus,
    isMonthEditable,
    // The current version (same for all months now)
    currentWorkingVersion: currentVersion,
  };
}

export function useSelectedAccount() {
  const { state } = useForecastV3();
  return state.selectedAccount;
}

export function useActiveTab() {
  const { state, setActiveTab } = useForecastV3();
  return { activeTab: state.activeTab, setActiveTab };
}

export function useDetailPanel() {
  const { state, toggleDetailPanel, setDetailPanelTab } = useForecastV3();
  return {
    isOpen: state.isDetailPanelOpen,
    activeTab: state.detailPanelTab,
    toggle: toggleDetailPanel,
    setTab: setDetailPanelTab,
  };
}

export function useFilters() {
  const { state, setFilters, clearFilters } = useForecastV3();
  return {
    filters: state.filters,
    setFilters,
    clearFilters,
    hasActiveFilters:
      state.filters.departments.length > 0 ||
      state.filters.accountTypes.length > 0 ||
      state.filters.driverTypes.length > 0 ||
      state.filters.showOnlyOverrides ||
      state.filters.showNeedsAttention ||
      state.filters.searchTerm.length > 0,
  };
}

export function useLoadingStates() {
  const { state, setLoading, setRecalculating, setSaving } = useForecastV3();
  return {
    isLoading: state.isLoading,
    isRecalculating: state.isRecalculating,
    isSaving: state.isSaving,
    setLoading,
    setRecalculating,
    setSaving,
  };
}

/**
 * Hook for bulk account selection operations.
 * Used for multi-select driver updates.
 */
export function useBulkSelection() {
  const { state, dispatch } = useForecastV3();
  const { isBulkMode, selectedAccountIds, bulkOperationInProgress } = state;

  const toggleBulkMode = useCallback((enabled?: boolean) => {
    dispatch({ type: 'TOGGLE_BULK_MODE', payload: enabled });
  }, [dispatch]);

  const toggleAccountSelection = useCallback((chartOfAccountId: number) => {
    dispatch({ type: 'TOGGLE_ACCOUNT_SELECTION', payload: chartOfAccountId });
  }, [dispatch]);

  const setSelectedAccounts = useCallback((ids: number[]) => {
    dispatch({ type: 'SET_SELECTED_ACCOUNTS', payload: ids });
  }, [dispatch]);

  const addSelectedAccounts = useCallback((ids: number[]) => {
    dispatch({ type: 'ADD_SELECTED_ACCOUNTS', payload: ids });
  }, [dispatch]);

  const clearSelection = useCallback(() => {
    dispatch({ type: 'CLEAR_BULK_SELECTION' });
  }, [dispatch]);

  const setBulkOperationInProgress = useCallback((inProgress: boolean) => {
    dispatch({ type: 'SET_BULK_OPERATION_IN_PROGRESS', payload: inProgress });
  }, [dispatch]);

  const isSelected = useCallback((chartOfAccountId: number) => {
    return selectedAccountIds.includes(chartOfAccountId);
  }, [selectedAccountIds]);

  return {
    isBulkMode,
    selectedAccountIds,
    selectedCount: selectedAccountIds.length,
    bulkOperationInProgress,
    toggleBulkMode,
    toggleAccountSelection,
    setSelectedAccounts,
    addSelectedAccounts,
    clearSelection,
    setBulkOperationInProgress,
    isSelected,
  };
}

/**
 * Hook for expanded column operations.
 * Controls which month columns show Budget/Actual/Forecast sub-columns.
 */
export function useExpandedColumns() {
  const { state, dispatch } = useForecastV3();
  const { expandedColumns } = state;

  const toggleColumnExpansion = useCallback((monthIndex: number) => {
    dispatch({ type: 'TOGGLE_COLUMN_EXPANSION', payload: monthIndex });
  }, [dispatch]);

  const expandAllColumns = useCallback(() => {
    dispatch({ type: 'SET_ALL_COLUMNS_EXPANDED', payload: true });
  }, [dispatch]);

  const collapseAllColumns = useCallback(() => {
    dispatch({ type: 'SET_ALL_COLUMNS_EXPANDED', payload: false });
  }, [dispatch]);

  const isColumnExpanded = useCallback((monthIndex: number) => {
    return expandedColumns[monthIndex] === true;
  }, [expandedColumns]);

  const isAnyExpanded = useMemo(() => {
    return Object.values(expandedColumns).some(Boolean);
  }, [expandedColumns]);

  return {
    expandedColumns,
    isColumnExpanded,
    isAnyExpanded,
    toggleColumnExpansion,
    expandAllColumns,
    collapseAllColumns,
  };
}

// ============ URL Sync Component ============

interface ForecastUrlSyncProps {
  children: ReactNode;
  /** Callback when URL wants to select a specific version by ID */
  onVersionIdFromUrl?: (versionId: number) => void;
  /** Callback when URL wants to select a specific account by ID */
  onAccountIdFromUrl?: (accountId: number) => void;
}

/**
 * ForecastUrlSync - Syncs context state to/from URL query parameters
 *
 * Must be used inside ForecastV3Provider. Handles:
 * - Initializing state from URL on mount (if URL has params)
 * - Syncing state changes to URL (replace mode to avoid history spam)
 * - Browser back/forward navigation
 *
 * Version and account loading from URL IDs is delegated to parent via callbacks,
 * since the actual version/account objects need to be fetched from API.
 */
export function ForecastUrlSync({
  children,
  onVersionIdFromUrl,
  onAccountIdFromUrl,
}: ForecastUrlSyncProps) {
  const {
    state,
    setActiveTab,
    setYear,
    setActiveMonth,
    setDetailPanelTab,
  } = useForecastV3();

  const { urlState, updateUrl, hasUrlParams } = useUrlState();

  // Track if initial sync from URL has been done
  const isInitializedRef = useRef(false);
  // Track if we're currently applying URL state (to prevent sync loops)
  const isApplyingUrlStateRef = useRef(false);
  // Track previous state for comparison
  const prevStateRef = useRef<Partial<UrlState>>({});

  // Initialize from URL on mount (only if URL has params)
  useEffect(() => {
    if (!isInitializedRef.current && hasUrlParams) {
      isApplyingUrlStateRef.current = true;

      // Apply simple state directly
      if (urlState.activeTab) {
        setActiveTab(urlState.activeTab);
      }
      if (urlState.year) {
        setYear(urlState.year);
      }
      if (urlState.activeMonth) {
        setActiveMonth(urlState.activeMonth);
      }
      if (urlState.detailPanelTab) {
        setDetailPanelTab(urlState.detailPanelTab);
      }

      // Delegate version and account loading to parent
      // (they need to be fetched from API, not just set by ID)
      if (urlState.versionId && onVersionIdFromUrl) {
        onVersionIdFromUrl(urlState.versionId);
      }
      if (urlState.selectedAccountId && onAccountIdFromUrl) {
        onAccountIdFromUrl(urlState.selectedAccountId);
      }

      isApplyingUrlStateRef.current = false;
      isInitializedRef.current = true;
    } else if (!isInitializedRef.current) {
      // No URL params, just mark as initialized
      isInitializedRef.current = true;
    }
  }, [
    hasUrlParams,
    urlState,
    setActiveTab,
    setYear,
    setActiveMonth,
    setDetailPanelTab,
    onVersionIdFromUrl,
    onAccountIdFromUrl,
  ]);

  // Sync context state to URL when state changes
  useEffect(() => {
    // Don't sync if we're applying URL state or not initialized
    if (isApplyingUrlStateRef.current || !isInitializedRef.current) {
      return;
    }

    const currentState: Partial<UrlState> = {
      activeTab: state.activeTab,
      locationId: state.locationId,
      versionId: state.currentVersion?.id ?? null,
      year: state.year,
      activeMonth: state.activeMonth,
      selectedAccountId: state.selectedAccount?.chartOfAccountId ?? null,
      detailPanelTab: state.isDetailPanelOpen ? state.detailPanelTab : undefined,
    };

    // Check if state actually changed to avoid unnecessary URL updates
    const hasChanged =
      currentState.activeTab !== prevStateRef.current.activeTab ||
      currentState.locationId !== prevStateRef.current.locationId ||
      currentState.versionId !== prevStateRef.current.versionId ||
      currentState.year !== prevStateRef.current.year ||
      currentState.activeMonth !== prevStateRef.current.activeMonth ||
      currentState.selectedAccountId !== prevStateRef.current.selectedAccountId ||
      currentState.detailPanelTab !== prevStateRef.current.detailPanelTab;

    if (hasChanged) {
      updateUrl({
        activeTab: state.activeTab,
        locationId: state.locationId,
        versionId: state.currentVersion?.id ?? null,
        year: state.year,
        activeMonth: state.activeMonth,
        selectedAccountId: state.selectedAccount?.chartOfAccountId ?? null,
        detailPanelTab: state.isDetailPanelOpen ? state.detailPanelTab : 'driver',
      });
      prevStateRef.current = currentState;
    }
  }, [
    state.activeTab,
    state.locationId,
    state.currentVersion?.id,
    state.year,
    state.activeMonth,
    state.selectedAccount?.chartOfAccountId,
    state.isDetailPanelOpen,
    state.detailPanelTab,
    updateUrl,
  ]);

  return <>{children}</>;
}

/**
 * Hook to get URL state for initialization purposes
 * Useful when you need to know what the URL wants before context is ready
 */
export function useUrlStateForInit() {
  const { urlState, hasUrlParams } = useUrlState();
  return { urlState, hasUrlParams };
}

export default ForecastV3Context;
